import React, { useEffect, useMemo, useState } from "react";
import { adminApi } from "../api";
import EmailBlockEditor, { blocksToHtml, newBlock } from "./EmailBlockEditor";

// Accept an optional `api` prop so the same component can be mounted in the
// client portal against clientApi-backed routes. Defaults to adminApi so
// existing admin imports keep working.
import "./EmailsTab.css";
import "./EmailBlockEditor.css";

const TEMPLATE_TABS = [
  { key: "welcome",      label: "After New Join",     hint: "Sent once a new member completes signup." },
  { key: "renewal",      label: "After Renewal",      hint: "Sent once an existing member renews." },
  { key: "pass_reissue", label: "After Pass Re-issue", hint: "Sent when a digital pass is issued or re-issued from the portal/admin." },
];

/* Fallback block list when an existing template has raw HTML but no
   structured blocks — the admin starts fresh rather than parsing HTML. */
const FALLBACK_BLOCKS = [
  newBlock("heading"),
  newBlock("text"),
  newBlock("wallet"),
];

export default function EmailsTab({ venueId, venue, api = adminApi }) {
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState("");
  const [activeKey, setActiveKey]   = useState("welcome");
  const [variables, setVariables]   = useState([]);
  const [saving, setSaving]         = useState(false);
  const [saved, setSaved]           = useState(false);
  const [testing, setTesting]       = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [testTo, setTestTo]         = useState("");
  const [showHtml, setShowHtml]     = useState(false);
  const [senderStatus, setSenderStatus] = useState(null);

  // Per-template state: { subject, blocks, savedSubject, savedBlocks, is_custom, updated_at }
  const [templates, setTemplates]   = useState({});
  const [preview, setPreview]       = useState({ subject: "", fullHtml: "" });

  /* ── Probe SendGrid sender readiness once on mount ── */
  useEffect(() => {
    if (!venueId) return;
    api.getEmailSenderStatus(venueId)
      .then((s) => setSenderStatus(s))
      .catch(() => {});
  }, [venueId]);

  /* ── Load both templates on mount ─────────────────── */
  useEffect(() => {
    if (!venueId) return;
    setLoading(true);
    setError("");
    api.getEmailTemplates(venueId)
      .then((res) => {
        const map = {};
        for (const t of res.templates) {
          const blocks = Array.isArray(t.body_blocks) && t.body_blocks.length > 0
            ? t.body_blocks
            : FALLBACK_BLOCKS;
          map[t.template_key] = {
            subject:      t.subject,
            blocks,
            savedSubject: t.subject,
            savedBlocks:  JSON.stringify(blocks),
            is_custom:    !!t.is_custom,
            updated_at:   t.updated_at,
          };
        }
        setTemplates(map);
        setVariables(res.variables || []);
      })
      .catch((e) => setError(e.message || "Failed to load templates"))
      .finally(() => setLoading(false));
  }, [venueId]);

  const tpl = templates[activeKey];
  const currentHtml = useMemo(() => tpl ? blocksToHtml(tpl.blocks) : "", [tpl]);
  const dirty = !!tpl && (
    tpl.subject !== tpl.savedSubject ||
    JSON.stringify(tpl.blocks) !== tpl.savedBlocks
  );

  /* ── Re-render preview on every change (debounced) ── */
  useEffect(() => {
    if (!tpl) return;
    let cancelled = false;
    const h = setTimeout(() => {
      api.previewEmailTemplate(venueId, activeKey, {
        subject:   tpl.subject,
        body_html: currentHtml,
      })
        .then((r) => { if (!cancelled) setPreview(r); })
        .catch(() => {});
    }, 200);
    return () => { cancelled = true; clearTimeout(h); };
  }, [venueId, activeKey, tpl?.subject, currentHtml]); // eslint-disable-line

  const updateTpl = (patch) => {
    setTemplates((p) => ({ ...p, [activeKey]: { ...p[activeKey], ...patch } }));
    setSaved(false);
  };

  const save = async () => {
    if (!tpl || !dirty) return;
    setSaving(true);
    setError("");
    try {
      const body_html = blocksToHtml(tpl.blocks);
      await api.saveEmailTemplate(venueId, activeKey, {
        subject:     tpl.subject,
        body_html,
        body_blocks: tpl.blocks,
      });
      setTemplates((p) => ({
        ...p,
        [activeKey]: {
          ...p[activeKey],
          savedSubject: tpl.subject,
          savedBlocks:  JSON.stringify(tpl.blocks),
          is_custom:    true,
        },
      }));
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e) {
      setError(e.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const revert = () => {
    if (!tpl) return;
    setTemplates((p) => ({
      ...p,
      [activeKey]: {
        ...p[activeKey],
        subject: p[activeKey].savedSubject,
        blocks:  JSON.parse(p[activeKey].savedBlocks),
      },
    }));
    setSaved(false);
  };

  const sendTest = async () => {
    if (!testTo || !tpl) return;
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.sendTestEmail(venueId, activeKey, {
        to_email:  testTo,
        subject:   tpl.subject,
        body_html: currentHtml,
      });
      setTestResult({ ok: true, message: r.message });
    } catch (e) {
      // api.js surfaces `.error` as the thrown message; our backend puts
      // the full SendGrid reason there when the 403 fires.
      setTestResult({ ok: false, message: e.message || "Send failed" });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="emails-loading">Loading templates…</div>;
  if (!tpl)    return <div className="su-alert error">No templates available.</div>;

  return (
    <div className="emails-tab">
      {error && <div className="su-alert error">⚠ {error}</div>}

      <div className="emails-header">
        <div>
          <h2 style={{ margin: 0 }}>Transactional Emails</h2>
          <p className="section-desc">
            Branded emails fired through SendGrid when a member joins or renews.
            Drop in the <strong>Wallet panel</strong> block to include the
            digital card QR + wallet buttons when Litecard is enabled.
          </p>
        </div>
      </div>

      {senderStatus && !senderStatus.ok && (
        <div className="su-alert error" style={{ alignItems: "flex-start", flexDirection: "column", gap: "0.4rem" }}>
          <div><strong>⚠ SendGrid sender not verified</strong></div>
          <div style={{ fontSize: "0.86rem", lineHeight: 1.55 }}>
            Emails cannot be sent until the <code>from</code> address is
            verified. Current address: <code>{senderStatus.from_email || "(not set)"}</code>.
          </div>
          <div style={{ fontSize: "0.86rem", lineHeight: 1.55 }}>
            In the SendGrid dashboard, either verify this single sender
            (Settings → Sender Authentication → Verify a Single Sender), or
            authenticate the full <code>{(senderStatus.from_email || "").split("@")[1] || "domain"}</code> domain
            (Settings → Sender Authentication → Authenticate Your Domain).
          </div>
        </div>
      )}

      <div className="emails-tabs">
        {TEMPLATE_TABS.map((t) => (
          <button
            key={t.key}
            type="button"
            className={`emails-tab-btn ${activeKey === t.key ? "active" : ""}`}
            onClick={() => setActiveKey(t.key)}
          >
            <span className="emails-tab-label">{t.label}</span>
            <span className="emails-tab-hint">{t.hint}</span>
            {templates[t.key]?.is_custom && <span className="emails-tab-badge">Custom</span>}
          </button>
        ))}
      </div>

      <div className="emails-split">
        {/* ── Editor column ──────────────────────── */}
        <div className="emails-editor">
          <div className="form-field">
            <label>Subject line</label>
            <input
              type="text"
              value={tpl.subject}
              onChange={(e) => updateTpl({ subject: e.target.value })}
              placeholder="Welcome to {{venue_name}}"
            />
          </div>

          <EmailBlockEditor
            blocks={tpl.blocks}
            onChange={(blocks) => updateTpl({ blocks })}
            variables={variables}
          />

          <div className="emails-html-toggle">
            <button type="button" className="btn-link" onClick={() => setShowHtml((v) => !v)}>
              {showHtml ? "▾ Hide generated HTML" : "▸ Show generated HTML"}
            </button>
            {showHtml && (
              <pre className="emails-html-snapshot">{currentHtml}</pre>
            )}
          </div>

          <div className="emails-actions">
            <div className="emails-test-row">
              <input
                type="email"
                placeholder="you@example.com"
                value={testTo}
                onChange={(e) => setTestTo(e.target.value)}
              />
              <button
                type="button"
                className="btn btn-outline"
                onClick={sendTest}
                disabled={!testTo || testing}
              >
                {testing ? "Sending…" : "Send test"}
              </button>
            </div>
            <div className="emails-save-row">
              {dirty && <button type="button" className="btn btn-ghost" onClick={revert}>Revert</button>}
              <button
                type="button"
                className="btn btn-primary"
                onClick={save}
                disabled={!dirty || saving}
              >
                {saving ? "Saving…" : saved ? "✓ Saved" : "Save template"}
              </button>
            </div>
          </div>

          {testResult && (
            <div className={`su-alert ${testResult.ok ? "success" : "error"}`} style={{ marginTop: "0.75rem" }}>
              {testResult.ok ? "✅" : "⚠"} {testResult.message}
            </div>
          )}
        </div>

        {/* ── Preview column ─────────────────────── */}
        <div className="emails-preview">
          <div className="emails-preview-subject">
            <span className="emails-preview-subject-label">Subject preview</span>
            <span className="emails-preview-subject-text">{preview.subject || tpl.subject}</span>
          </div>
          <div className="emails-preview-frame">
            <iframe
              title="Email preview"
              srcDoc={preview.fullHtml || ""}
              sandbox=""
            />
          </div>
          <p className="emails-preview-hint">
            Rendered with sample data and {venue?.name ? <strong>{venue.name}</strong> : "venue"} branding.
          </p>
        </div>
      </div>
    </div>
  );
}
